import {
  JOB_TABS,
  clientContacts,
  formatJobNumber,
  groupByWorklist,
  siteContacts,
  worklistStatus,
  type JobTabKey,
} from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AgreementBuilder } from '../components/agreements/AgreementBuilder.js'
import { ClientEditDialog, SiteDialog, type ClientRecord } from '../components/client/ClientDialogs.js'
import { ContactList } from '../components/client/ContactList.js'
import { Icon } from '../components/Icon.js'
import { NewJobDialog } from '../components/NewJobDialog.js'
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  INVOICE_STATUS_TONE,
  ListHead,
  Page,
  PageHeader,
  Segmented,
  SkeletonRows,
  StatStrip,
  StatTile,
  humanise,
  listRowClass,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { addressLine, formatDate, money, relativeTime } from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC } from '../lib/trpc.js'
import { AGREEMENT_STATUS, termLabel } from './Agreements.js'
import { jobPlace } from './Jobs.js'

/**
 * One client, as a page.
 *
 * Unlike a job, a client is a record somebody sits with — correcting a
 * billing address, adding the strata manager, retiring a property that was
 * typed in twice — so it is a route rather than an overlay (decision 61 is
 * about jobs). It leads with the money and the open work, because the two
 * questions asked of a client on the phone are "what do they owe" and "what
 * have we got on for them".
 *
 * The jobs card has the Jobs list's three stages. It used to call
 * `jobs.list` with no tab, which defaults to Work Order, so a client's quotes
 * and finished jobs never appeared on their own page.
 */

type Contact = ClientRecord['contacts'][number]
type Site = ClientRecord['sites'][number]

export function ClientDetail() {
  const { id = '' } = useParams()
  const trpc = useTRPC()
  const { can } = useSession()
  const queryClient = useQueryClient()

  const { data: client, isPending, error, refetch } = useQuery(
    trpc.clients.byId.queryOptions({ id }),
  )
  const stats = useQuery(trpc.clients.stats.queryOptions({ id }))

  const [editing, setEditing] = useState(false)
  const [newJob, setNewJob] = useState(false)
  const [site, setSite] = useState<Site | null | undefined>(undefined)

  const archive = useMutation(
    trpc.clients.setArchived.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.clients.pathFilter()),
    }),
  )

  if (error) {
    return (
      <Page>
        <Card>
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        </Card>
      </Page>
    )
  }

  if (isPending) {
    return (
      <Page>
        <Card>
          <SkeletonRows />
        </Card>
      </Page>
    )
  }

  const editable = can('CLIENT_EDIT')
  const liveSites = client.sites.filter((entry) => !entry.archivedAt)
  const archivedSites = client.sites.filter((entry) => entry.archivedAt)

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/clients" className="inline-flex items-center gap-1 hover:text-white">
            <Icon name="back" className="size-3.5" />
            Clients
            <span className="text-sky/60">· Client {client.number}</span>
          </Link>
        }
        title={client.name}
        description={
          <span className="flex flex-wrap items-center gap-1.5">
            {/* The shell's header carries the page name on a phone, so the
                client's own name rides here there. */}
            <span className="font-semibold text-ink-900 desk:hidden">{client.name}</span>
            <ClientBadges client={client} />
          </span>
        }
        actions={
          <>
            {editable ? (
              <Button onClick={() => setEditing(true)} icon={<Icon name="edit" className="size-4" />}>
                Edit
              </Button>
            ) : null}
            {can('JOB_EDIT') ? (
              <Button
                variant="primary"
                className="rounded-full"
                onClick={() => setNewJob(true)}
                icon={<Icon name="plus" className="size-4" />}
              >
                New job
              </Button>
            ) : null}
          </>
        }
      />

      {client.archivedAt ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
          <span className="min-w-0 flex-1">
            Archived {formatDate(client.archivedAt)}. It is hidden from the list and the pickers.
          </span>
          {editable ? (
            <Button
              size="sm"
              loading={archive.isPending}
              onClick={() => archive.mutate({ id: client.id, archived: false })}
            >
              Restore client
            </Button>
          ) : null}
        </div>
      ) : null}

      <StatStrip>
        <StatTile label="Open jobs" value={stats.data?.openJobs ?? '·'} caption="Not finished" />
        <StatTile label="All jobs" value={stats.data?.jobs ?? '·'} caption="Ever" />
        <StatTile
          label="Owing"
          value={stats.data ? money(stats.data.outstanding) : '·'}
          caption="Issued, not settled"
        />
        <StatTile
          label="Overdue"
          value={stats.data ? money(stats.data.overdue) : '·'}
          tone={stats.data && stats.data.overdue > 0 ? 'red' : undefined}
          caption="Past its due date"
        />
        {stats.data?.nextVisitAt ? (
          <StatTile
            label="Next visit"
            value={relativeTime(stats.data.nextVisitAt)}
            caption={formatDate(stats.data.nextVisitAt)}
          />
        ) : (
          <StatTile
            label="Last job"
            value={stats.data?.lastJobAt ? relativeTime(stats.data.lastJobAt) : '—'}
            caption={stats.data?.lastJobAt ? formatDate(stats.data.lastJobAt) : 'No jobs yet'}
          />
        )}
      </StatStrip>

      <div className="grid gap-[var(--stack-gap)] desk:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)] desk:items-start">
        <div className="flex min-w-0 flex-col gap-[var(--stack-gap)]">
          <DetailsCard client={client} onEdit={editable ? () => setEditing(true) : undefined} />
          <Card>
            <CardHeader title={`Contacts (${clientContacts(client.contacts).length})`} />
            {/* The client's own people; a property's are listed under it.
                Decision 85. */}
            <ContactList
              clientId={client.id}
              siteId={null}
              people={clientContacts(client.contacts)}
              canEdit={editable}
            />
          </Card>
          {editable && !client.archivedAt ? (
            <button
              type="button"
              onClick={() => archive.mutate({ id: client.id, archived: true })}
              className="self-start rounded-md px-1 text-xs text-ink-500 hover:text-ink-900"
            >
              Archive this client…
            </button>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-[var(--stack-gap)]">
          <SitesCard
            sites={liveSites}
            archived={archivedSites}
            contacts={client.contacts}
            clientId={client.id}
            editable={editable}
            onAdd={() => setSite(null)}
            onEdit={setSite}
          />
          <JobsCard clientId={client.id} />
          <AgreementsCard client={client} canEdit={editable} />
          {can('INVOICE_VIEW') ? <InvoicesCard clientId={client.id} /> : null}
        </div>
      </div>

      <ClientEditDialog client={client} open={editing} onClose={() => setEditing(false)} />
      <SiteDialog
        clientId={client.id}
        site={site ?? null}
        open={site !== undefined}
        onClose={() => setSite(undefined)}
      />
      <NewJobDialog
        open={newJob}
        onClose={() => setNewJob(false)}
        client={{ id: client.id, name: client.name, number: client.number, type: client.type }}
      />
    </Page>
  )
}

function ClientBadges({ client }: { client: ClientRecord }) {
  return (
    <>
      <Badge>{humanise(client.type)}</Badge>
      {client.isOnCreditHold ? <Badge tone="red">Credit hold</Badge> : null}
      {client.priceTier ? <Badge tone="purple">{client.priceTier.name}</Badge> : null}
      {client.taxExempt ? <Badge tone="blue">Tax exempt</Badge> : null}
      {client.archivedAt ? <Badge tone="amber">Archived</Badge> : null}
    </>
  )
}

function DetailsCard({ client, onEdit }: { client: ClientRecord; onEdit?: () => void }) {
  return (
    <Card>
      <CardHeader
        title="Details"
        action={
          onEdit ? (
            <Button size="sm" variant="ghost" onClick={onEdit}>
              Edit
            </Button>
          ) : null
        }
      />
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 p-[var(--card-pad)] sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        <Detail label="Payment terms">
          {client.paymentTermsDays === 0 ? 'Due on receipt' : `${client.paymentTermsDays} days`}
        </Detail>
        <Detail label="ABN">{client.taxNumber ?? <Missing />}</Detail>
        <Detail label="Billing address" className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
          {client.billingAddress ? addressLine(client.billingAddress) : <Missing />}
        </Detail>
        {client.website ? (
          <Detail label="Website" className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
            {client.website}
          </Detail>
        ) : null}
        {client.notes ? (
          <Detail label="Office notes" className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
            <span className="whitespace-pre-line">{client.notes}</span>
          </Detail>
        ) : null}
      </dl>
    </Card>
  )
}

function Missing() {
  return <span className="text-ink-400">Not recorded</span>
}

function SitesCard({
  sites,
  archived,
  contacts,
  clientId,
  editable,
  onAdd,
  onEdit,
}: {
  sites: Site[]
  archived: Site[]
  contacts: Contact[]
  clientId: string
  editable: boolean
  onAdd: () => void
  onEdit: (site: Site) => void
}) {
  const [showArchived, setShowArchived] = useState(false)

  return (
    <Card>
      <CardHeader
        title={`Sites (${sites.length})`}
        action={
          editable ? (
            <Button size="sm" variant="ghost" onClick={onAdd} icon={<Icon name="plus" className="size-4" />}>
              Add site
            </Button>
          ) : null
        }
      />
      {sites.length === 0 ? (
        <EmptyState
          icon="map"
          title="No sites yet"
          description="A site is a property where the work happens. Each keeps its own access notes, hazards, contacts and service history."
        />
      ) : (
        <ul className="divide-y divide-ink-100">
          {sites.map((entry) => (
            <SiteRow
              key={entry.id}
              site={entry}
              people={siteContacts(contacts, entry.id)}
              clientId={clientId}
              editable={editable}
              onEdit={onEdit}
            />
          ))}
        </ul>
      )}
      {archived.length > 0 ? (
        <div className="border-t border-ink-100">
          <button
            type="button"
            onClick={() => setShowArchived((open) => !open)}
            aria-expanded={showArchived}
            className="flex w-full items-center gap-1.5 px-[var(--row-pad-x)] py-2 text-xs font-medium text-ink-500 hover:text-ink-900"
          >
            <Icon
              name="chevronDown"
              className={cn('size-3.5 transition-transform', !showArchived && '-rotate-90')}
            />
            {archived.length} archived {archived.length === 1 ? 'site' : 'sites'}
          </button>
          {showArchived ? (
            <ul className="divide-y divide-ink-100 opacity-70">
              {archived.map((entry) => (
                <SiteRow
                  key={entry.id}
                  site={entry}
                  people={siteContacts(contacts, entry.id)}
                  clientId={clientId}
                  editable={editable}
                  onEdit={onEdit}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}

function SiteRow({
  site,
  people,
  clientId,
  editable,
  onEdit,
}: {
  site: Site
  people: Contact[]
  clientId: string
  editable: boolean
  onEdit: (site: Site) => void
}) {
  const address = addressLine(site.address)
  const jobs = site._count?.jobs ?? 0

  return (
    <li>
    <div className="flex items-start gap-2.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
      <span className="mt-px grid size-6 shrink-0 place-items-center rounded-md bg-ink-100 text-ink-500">
        <Icon name="map" className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        <p className="min-w-0 text-sm font-medium break-words text-ink-900">{site.name ?? address}</p>
        {site.name ? <p className="min-w-0 text-xs break-words text-ink-500">{address}</p> : null}
        <div className="flex flex-wrap gap-1.5">
          <Badge>
            {jobs} {jobs === 1 ? 'job' : 'jobs'}
          </Badge>
          {site.hazardNotes ? (
            <Badge tone="amber" className="max-w-full truncate">
              Hazard: {site.hazardNotes}
            </Badge>
          ) : null}
          {site.gateCode ? <Badge tone="neutral">Code {site.gateCode}</Badge> : null}
          {site.archivedAt ? <Badge tone="amber">Archived</Badge> : null}
        </div>
        {site.accessNotes ? (
          <p className="line-clamp-2 basis-full text-xs text-ink-600">{site.accessNotes}</p>
        ) : null}
      </div>
      {editable ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(site)}
          aria-label={`Edit ${site.name ?? address}`}
        >
          Edit
        </Button>
      ) : null}
    </div>
    {/* The people at this property — a tenant, a site manager. Never the
        client's primary. Decision 85. */}
    <div className="pl-9">
      <ContactList clientId={clientId} siteId={site.id} people={people} canEdit={editable} />
    </div>
    </li>
  )
}

const JOB_GRID = 'sm:grid-cols-[5.5rem_minmax(0,1fr)_minmax(0,1.4fr)_7.5rem_6.5rem]'

function JobsCard({ clientId }: { clientId: string }) {
  const trpc = useTRPC()
  const jobHref = useJobHref()
  const [tab, setTab] = useState<JobTabKey>('WORK_ORDER')

  const counts = useQuery(trpc.jobs.worklistCounts.queryOptions({ clientId }))
  const jobs = useQuery(trpc.jobs.list.queryOptions({ clientId, tab, perPage: 20 }))

  const now = useMemo(() => new Date(), [jobs.data])
  const rows = useMemo(
    () => groupByWorklist(jobs.data?.items ?? [], tab, now).flatMap((group) =>
      group.items.map((job) => ({ job, status: group.key })),
    ),
    [jobs.data?.items, tab, now],
  )

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-[var(--card-pad)] py-2">
        <h2 className="mr-auto font-display text-[15px] leading-snug font-bold text-brand-800">Jobs</h2>
        <Segmented
          label="Job stage"
          value={tab}
          onChange={setTab}
          options={JOB_TABS.map((entry) => ({
            value: entry.key,
            label: entry.label,
            count: counts.data?.byTab[entry.key],
          }))}
        />
      </div>
      {jobs.isPending ? (
        <SkeletonRows rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState icon="jobs" title="Nothing at this stage" />
      ) : (
        <>
          <ListHead className={JOB_GRID}>
            <span>Job</span>
            <span>Work</span>
            <span>Property</span>
            <span>Status</span>
            <span className="text-right">Total</span>
          </ListHead>
          <ul className="divide-y divide-ink-100">
            {rows.map(({ job, status }) => {
              const entry = worklistStatus(status)
              return (
                <li key={job.id}>
                  <Link to={jobHref(job.id)} className={cn(listRowClass, JOB_GRID)}>
                    <span className="text-xs font-medium tabular-nums text-ink-500">
                      {formatJobNumber(job)}
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5 text-xs text-ink-600">
                      {job.category ? (
                        <>
                          <span
                            aria-hidden="true"
                            className="size-2 shrink-0 rounded-full"
                            style={{ backgroundColor: job.category.colour }}
                          />
                          <span className="truncate">{job.category.name}</span>
                        </>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </span>
                    <span className="order-last basis-full text-sm break-words text-ink-900 sm:order-none sm:basis-auto sm:truncate">
                      {jobPlace(job)}
                    </span>
                    <span>
                      <Badge tone={entry.tone}>{entry.label}</Badge>
                    </span>
                    <span
                      className={cn(
                        'ml-auto text-sm font-medium tabular-nums text-ink-900 sm:ml-0 sm:text-right',
                        !job.totalIncTax && 'hidden sm:block',
                      )}
                    >
                      {job.totalIncTax ? money(job.totalIncTax) : '—'}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
          {jobs.data && jobs.data.total > rows.length ? (
            <p className="border-t border-ink-100 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
              Showing the latest {rows.length} of {jobs.data.total}.
            </p>
          ) : null}
        </>
      )}
    </Card>
  )
}

const INVOICE_GRID = 'sm:grid-cols-[7rem_minmax(0,1fr)_6.5rem_6.5rem_6rem]'

function InvoicesCard({ clientId }: { clientId: string }) {
  const trpc = useTRPC()
  const jobHref = useJobHref()
  const invoices = useQuery(trpc.invoices.list.queryOptions({ clientId, perPage: 10 }))
  const now = new Date()

  return (
    <Card>
      <CardHeader
        title="Invoices"
        action={
          <Link
            to={`/invoices?clientId=${clientId}`}
            className="text-xs font-medium text-brand-700 hover:underline"
          >
            All invoices
          </Link>
        }
      />
      {invoices.isPending ? (
        <SkeletonRows rows={2} />
      ) : !invoices.data || invoices.data.items.length === 0 ? (
        <EmptyState icon="invoices" title="No invoices yet" />
      ) : (
        <>
          <ListHead className={INVOICE_GRID}>
            <span>Number</span>
            <span>Issued</span>
            <span className="text-right">Total</span>
            <span className="text-right">Owing</span>
            <span className="text-right">Status</span>
          </ListHead>
          <ul className="divide-y divide-ink-100">
            {invoices.data.items.map((invoice) => {
              const owing = invoice.totalIncTax - invoice.amountPaid
              const overdue =
                invoice.dueAt && new Date(invoice.dueAt) < now && owing > 0 && invoice.status !== 'VOID'
              const row: ReactNode = (
                <>
                  <span className="text-xs font-medium tabular-nums text-ink-700">{invoice.number}</span>
                  <span className="text-xs text-ink-500">{formatDate(invoice.issuedAt)}</span>
                  <span className="text-sm tabular-nums text-ink-900 sm:text-right">
                    {money(invoice.totalIncTax)}
                  </span>
                  <span
                    className={cn(
                      'text-sm tabular-nums sm:text-right',
                      owing > 0 ? 'font-medium text-ink-900' : 'text-ink-400',
                    )}
                  >
                    {owing > 0 ? money(owing) : 'Settled'}
                  </span>
                  <span className="sm:text-right">
                    <Badge tone={overdue ? 'red' : (INVOICE_STATUS_TONE[invoice.status] ?? 'neutral')}>
                      {overdue ? 'Overdue' : humanise(invoice.status)}
                    </Badge>
                  </span>
                </>
              )
              return (
                <li key={invoice.id}>
                  {invoice.job ? (
                    <Link to={jobHref(invoice.job.id)} className={cn(listRowClass, INVOICE_GRID)}>
                      {row}
                    </Link>
                  ) : (
                    <div className={cn(listRowClass, INVOICE_GRID)}>{row}</div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Card>
  )
}

/** The client's service agreements, and a way to start one. Decision 96. */
function AgreementsCard({
  client,
  canEdit,
}: {
  client: { id: string; name: string }
  canEdit: boolean
}) {
  const trpc = useTRPC()
  const clientId = client.id
  const [creating, setCreating] = useState(false)
  const agreements = useQuery(trpc.agreements.list.queryOptions({ clientId }))

  return (
    <Card>
      <CardHeader
        title="Service agreements"
        action={
          canEdit ? (
            <Button size="sm" variant="ghost" onClick={() => setCreating(true)}>
              New agreement
            </Button>
          ) : null
        }
      />
      {agreements.isPending ? (
        <SkeletonRows rows={2} />
      ) : (agreements.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon="agreement"
          title="No service agreements"
          description="An agreement puts this client's sites and their regular services under one term."
        />
      ) : (
        <ul className="divide-y divide-ink-100">
          {agreements.data!.map((agreement) => (
            <li key={agreement.id}>
              <Link
                to={`/agreements/${agreement.id}`}
                className="flex min-h-[var(--control-md)] items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50"
              >
                <span className="w-16 shrink-0 font-mono text-xs text-ink-500">{`AG-${agreement.number}`}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink-900">{agreement.name}</span>
                <span className="shrink-0 text-xs text-ink-600 tabular-nums">{termLabel(agreement)}</span>
                <Badge tone={AGREEMENT_STATUS[agreement.status].tone}>
                  {AGREEMENT_STATUS[agreement.status].label}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <AgreementBuilder
        open={creating}
        mode={{ kind: 'new', client: { id: client.id, name: client.name } }}
        onClose={() => setCreating(false)}
      />
    </Card>
  )
}
